import type { IActiveWorkout, IPRRecord } from '@/hooks/useAppState';
import type { WorkoutExerciseRecord } from './workoutProgress';
import { workoutExerciseIds, type WorkoutSource } from './workoutSource';

export interface WorkoutDraft {
  version: 1;
  owner: string;
  dayId: string;
  courseId: string;
  startTimestamp: number;
  route: string;
  records: WorkoutExerciseRecord[];
  exerciseIndex: number;
  setIndex: number;
  resting: boolean;
  restEndsAt: number;
  restTotal: number;
  editWeight: string;
  editReps: string;
  feelingOpen: boolean;
  finishedRecordId: string | null;
  newPRs: IPRRecord[];
}

type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export const workoutDraftKey = (owner: string) => `lianke:workout-local-draft:${owner}`;
export function workoutRouteKey(route: string): string {
  const [pathname, query = ''] = route.split('?');
  const parameters = new URLSearchParams(query);
  parameters.sort();
  return `${pathname}?${parameters.toString()}`;
}
export function remainingWorkoutRest(end: number, now = Date.now()): number {
  return Math.max(0, Math.ceil((end - now) / 1000));
}

export function canResumeWorkout(draft: WorkoutDraft | null, source: WorkoutSource | null, route: string): boolean {
  const ids = workoutExerciseIds(source);
  return !!draft && !!source && draft.dayId === source.day.id && draft.courseId === source.course.id
    && workoutRouteKey(draft.route) === workoutRouteKey(route) && draft.records.length === ids.length
    && draft.records.every((record, index) => record.exerciseId === ids[index]);
}

function validRecord(value: unknown): value is WorkoutExerciseRecord {
  if (!value || typeof value !== 'object') return false;
  const record = value as WorkoutExerciseRecord;
  return typeof record.exerciseId === 'string' && typeof record.exerciseName === 'string' && Array.isArray(record.sets)
    && record.sets.every(set => set && Number.isInteger(set.setNum) && Number.isFinite(set.reps)
      && typeof set.completed === 'boolean' && (Number.isFinite(set.weight) || (!set.completed && set.weight === null)));
}

/** Account-scoped local draft. Existing synced keys and business record formats stay untouched. */
export function readWorkoutDraft(storage: DraftStorage, owner: string, active: IActiveWorkout,
  ttl: number, now = Date.now()): WorkoutDraft | null {
  try {
    const raw = storage.getItem(workoutDraftKey(owner));
    if (!raw) return null;
    const draft = JSON.parse(raw) as WorkoutDraft;
    if (draft.version !== 1 || draft.owner !== owner
      || !Number.isFinite(draft.startTimestamp) || draft.startTimestamp <= 0 || now - draft.startTimestamp > ttl
      || typeof draft.courseId !== 'string' || typeof draft.route !== 'string' || !draft.route.startsWith('/workout/')
      || !Array.isArray(draft.records) || !draft.records.length || !draft.records.every(validRecord)
      || !Number.isInteger(draft.exerciseIndex) || draft.exerciseIndex < 0 || draft.exerciseIndex >= draft.records.length
      || !Number.isInteger(draft.setIndex) || draft.setIndex < 0
      || (draft.records[draft.exerciseIndex].sets.length > 0 && draft.setIndex >= draft.records[draft.exerciseIndex].sets.length)
      || typeof draft.resting !== 'boolean' || !Number.isFinite(draft.restEndsAt) || draft.restEndsAt < 0
      || !Number.isFinite(draft.restTotal) || draft.restTotal < 0
      || typeof draft.editWeight !== 'string' || typeof draft.editReps !== 'string' || typeof draft.feelingOpen !== 'boolean'
      || !(draft.finishedRecordId === null || typeof draft.finishedRecordId === 'string') || !Array.isArray(draft.newPRs)) {
      storage.removeItem(workoutDraftKey(owner));
      return null;
    }
    // Another page may still hold older metadata. Reading it must not delete the current draft.
    if (!active.active || draft.dayId !== active.dayId || (active.courseId && draft.courseId !== active.courseId)
      || draft.startTimestamp !== active.startTimestamp) return null;
    // JSON stores the pre-existing blank-weight NaN sentinel as null; restore it only on unfinished sets.
    return { ...draft, records: draft.records.map(record => ({ ...record,
      sets: record.sets.map(set => ({ ...set, weight: set.weight === null ? NaN : set.weight })) })) };
  } catch {
    try { storage.removeItem(workoutDraftKey(owner)); } catch { /* Storage may be unavailable. */ }
    return null;
  }
}

export function writeWorkoutDraft(storage: DraftStorage, draft: WorkoutDraft): boolean {
  try { storage.setItem(workoutDraftKey(draft.owner), JSON.stringify(draft)); return true; } catch { return false; }
}
export function clearWorkoutDraft(storage: DraftStorage, owner: string): void {
  try { storage.removeItem(workoutDraftKey(owner)); } catch { /* No writes to shared storage as a fallback. */ }
}
